import { useMemo } from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { DataTable } from '@/components/ui/table/data-table';

import type { McpServer, McpTool, TestResult } from './hooks/useMcpServers';
import { toMcpTool } from './utils';

type ToolDiffStatus = 'updated' | 'deleted';

type ToolDiff = { name: string; status: ToolDiffStatus };

const statusBadge = (status: ToolDiffStatus, t: TFunction) => {
  if (status === 'updated') {
    return (
      <Badge type="secondary" flavour="informational" rounded={false}>
        {t('mcp.tool_status_updated')}
      </Badge>
    );
  }
  return (
    <Badge type="secondary" flavour="destructive" rounded={false}>
      {t('mcp.tool_status_deleted')}
    </Badge>
  );
};

const createResyncColumns = (t: TFunction): ColumnDef<ToolDiff>[] => [
  {
    accessorKey: 'name',
    header: t('common.name'),
    cell: ({ row }) => <span className="truncate font-medium">{row.original.name}</span>,
    minSize: 150,
    maxSize: 400,
    meta: { preset: 'FLEX' },
  },
  {
    accessorKey: 'status',
    header: t('mcp.status'),
    cell: ({ row }) => statusBadge(row.original.status, t),
    size: 140,
    minSize: 120,
    maxSize: 160,
  },
];

// Recursively sort object keys for stable JSON comparison
export const sortKeys = (obj: unknown): unknown => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sortKeys);
  }
  return Object.keys(obj as Record<string, unknown>)
    .sort()
    .reduce(
      (acc, key) => {
        acc[key] = sortKeys((obj as Record<string, unknown>)[key]);
        return acc;
      },
      {} as Record<string, unknown>
    );
};

interface ResyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  server: McpServer | null;
  testResult: TestResult | undefined;
  isLoading: boolean;
  error: Error | null;
  onUpdate: (tools: McpTool[]) => void;
}

export const getAuthHeaders = (server: McpServer): Record<string, string> | undefined => {
  if (!server.config || typeof server.config !== 'object') {
    return undefined;
  }
  const customHeaders = 'headers' in server.config ? server.config.headers : undefined;
  if ('auth_type' in server.config && server.config.auth_type === 'oauth') {
    // OAuth Authorization overrides any custom header with the same key
    return { ...customHeaders, Authorization: `Bearer ${server.config.access_token}` };
  }
  return customHeaders;
};

const computeToolDiffs = (localTools: McpTool[], remoteTools: TestResult['tools']): ToolDiff[] => {
  const diffs: ToolDiff[] = [];
  const remoteMap = new Map((remoteTools ?? []).map((t) => [t.name, t]));

  // Only compare tools that exist locally (no "new" tools shown)
  for (const local of localTools) {
    const remote = remoteMap.get(local.name);
    if (!remote) {
      // Deleted: exists locally but not on remote
      diffs.push({ name: local.name, status: 'deleted' });
    } else {
      // Check if updated (normalize both for stable comparison)
      const localNormalized = sortKeys({
        name: local.name,
        description: local.description ?? '',
        inputSchema: local.inputSchema ?? {},
        annotations: local.annotations ?? {},
      });
      const remoteNormalized = sortKeys({
        name: remote.name,
        description: remote.description ?? '',
        inputSchema: remote.inputSchema ?? {},
        annotations: remote.annotations ?? {},
      });
      if (JSON.stringify(localNormalized) !== JSON.stringify(remoteNormalized)) {
        diffs.push({ name: local.name, status: 'updated' });
      }
    }
  }

  return diffs;
};

export function ResyncDialog({
  open,
  onOpenChange,
  server,
  testResult,
  isLoading,
  error,
  onUpdate,
}: ResyncDialogProps) {
  const { t } = useTranslation();

  const diffs = useMemo(() => {
    if (!server || !testResult?.tools) {
      return [];
    }
    return computeToolDiffs(server.tools, testResult.tools);
  }, [server, testResult?.tools]);

  const columns = useMemo(() => createResyncColumns(t), [t]);

  const handleUpdate = () => {
    if (!testResult?.tools || !server) {
      return;
    }
    const remoteMap = new Map(testResult.tools.map((t) => [t.name, t]));
    // Only update/delete existing tools, never add new ones
    const newTools: McpTool[] = server.tools
      .map((local) => {
        const remote = remoteMap.get(local.name);
        if (!remote) {
          // Deleted on remote - exclude
          return null;
        }
        return toMcpTool(remote);
      })
      .filter((t): t is McpTool => t !== null);
    onUpdate(newTools);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('mcp.resync_title', { 0: server?.name ?? '' })}</DialogTitle>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-2">
          <p className="px-1 text-xs font-medium text-muted-foreground">
            {t('mcp.list_of_updates')}
          </p>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
              {error.message}
            </div>
          )}

          {!isLoading && !error && diffs.length === 0 && (
            <div className="rounded-md border p-4 text-center text-sm text-muted-foreground">
              {t('mcp.no_changes')}
            </div>
          )}

          {!isLoading && !error && diffs.length > 0 && (
            <DataTable.Root
              columns={columns}
              data={diffs}
              rowCount={diffs.length}
              className="max-h-[400px] overflow-auto rounded-md border"
              getRowId={(row) => row.name}
            >
              <DataTable.Content />
            </DataTable.Root>
          )}

          {!isLoading && !error && diffs.length > 0 && (
            <p className="pl-3 text-sm font-medium text-muted-foreground">
              {t('mcp.updates_count', { 0: diffs.length })}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost-neutral" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleUpdate} disabled={isLoading || !!error || diffs.length === 0}>
            {t('mcp.resync')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
